import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Paths that are considered onboarding-related and should be skipped for redirect
const ONBOARDING_PATHS = [
  '/onboarding',
  '/smart-onboarding', 
  '/get-started',
  '/cosec/onboarding',
  '/grc/onboarding',
  '/risk/onboarding',
  '/ma/onboarding'
];

// Paths that should never trigger onboarding redirect (settings, subscription, etc.)
const EXEMPT_PATHS = [
  '/subscription',
  '/pricing',
  '/account',
  '/settings',
  '/logout'
];

export const ProtectedRoute = ({ children, requiresSubscription = true }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const onboardingChecked = useRef(false);

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate('/login', { replace: true });
    }
  }, [user, loading, navigate, location.state]);

  // Check onboarding status and redirect if needed
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user || loading || onboardingChecked.current) return;
      
      // Skip check if already on an onboarding page
      const isOnboardingPath = ONBOARDING_PATHS.some(path => location.pathname.startsWith(path));
      if (isOnboardingPath) {
        onboardingChecked.current = true;
        return;
      }
      
      // Skip check for exempt paths
      const isExemptPath = EXEMPT_PATHS.some(path => location.pathname.startsWith(path));
      if (isExemptPath) {
        onboardingChecked.current = true;
        return;
      }

      try {
        // Check user's onboarding status from backend
        const response = await axios.get(`${API}/auth/v2/me`, {
          withCredentials: true,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('session_token') || ''}`
          }
        });
        
        const userData = response.data;
        const onboardingCompleted = userData.onboarding_completed;
        const primaryModule = userData.primary_module || userData.signup_source || 'grc';
        
        onboardingChecked.current = true;
        
        // Redirect to appropriate onboarding based on primary module if not completed
        if (!onboardingCompleted) {
          let onboardingPath = '/grc/onboarding'; // Default fallback
          
          if (primaryModule === 'cosec' || primaryModule === 'company-secretary') {
            onboardingPath = '/cosec/onboarding';
          } else if (primaryModule === 'grc') {
            onboardingPath = '/grc/onboarding';
          } else if (primaryModule === 'ma') {
            onboardingPath = '/ma/onboarding';
          }
          
          console.log(`[ProtectedRoute] User onboarding not complete. Redirecting to ${onboardingPath}`);
          navigate(onboardingPath, { replace: true });
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        onboardingChecked.current = true; // Don't block on error
      }
    };

    if (user && !loading) {
      checkOnboardingStatus();
    }
  }, [user, loading, location.pathname, navigate]);

  useEffect(() => {
    const checkSubscription = async () => {
      if (!user) {
        setSubscriptionLoading(false);
        return;
      }

      // Allow onboarding pages without subscription check
      const isOnboardingPath = ONBOARDING_PATHS.some(path => location.pathname.startsWith(path));
      if (isOnboardingPath) {
        setSubscriptionLoading(false);
        setSubscriptionStatus({ is_active: true, requires_payment: false });
        return;
      }

      try {
        // Use withCredentials to send HttpOnly cookies
        const response = await axios.get(`${API}/subscription`, {
          withCredentials: true
        });
        setSubscriptionStatus(response.data);
        
        // Redirect to pricing if payment required and not already on pricing
        if (requiresSubscription && response.data.requires_payment && 
            !location.pathname.includes('/pricing') && 
            !location.pathname.includes('/subscription')) {
          navigate('/pricing', { replace: true });
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
        // If 402 Payment Required, redirect to pricing
        if (error.response?.status === 402) {
          navigate('/pricing', { replace: true });
        }
      } finally {
        setSubscriptionLoading(false);
      }
    };

    if (user && !loading) {
      checkSubscription();
    } else if (!loading && !user) {
      setSubscriptionLoading(false);
    }
  }, [user, loading, location.pathname, navigate, requiresSubscription]);

  if (loading || subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return null;
  }

  // Block access if payment required (except for onboarding)
  const isOnboardingPath = ONBOARDING_PATHS.some(path => location.pathname.startsWith(path));
  if (requiresSubscription && subscriptionStatus?.requires_payment && !isOnboardingPath) {
    return null;
  }

  return children;
};
